import { SignInButton, SignUpButton, UserButton, useUser } from "@clerk/clerk-react";
import { HelpCircle, LayoutDashboard, Settings, User } from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useCurrentUser } from "../context/CurrentUserContext";

interface NavigationItem {
	to: string;
	icon: React.ComponentType<{ className?: string }>;
	label: string;
	featureFlag?: string;
}

export default function Header() {
	const { isSignedIn, isLoaded } = useUser();
	const isTeamsEnabled = useFeatureFlagEnabled("teams");
	const { currentUser } = useCurrentUser();
	const isMember = currentUser?.role === "member";
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

	// Centralized navigation configuration
	const navigationItems: NavigationItem[] = [
		{
			to: "/",
			icon: LayoutDashboard,
			label: "Overview",
		},
		{
			to: "/account",
			icon: User,
			label: "Account",
		},
		{
			to: "/team",
			icon: User,
			label: "Team",
			featureFlag: "teams",
		},
		{
			to: "/settings",
			icon: Settings,
			label: "Settings",
			featureFlag: "settings",
		},
		{
			to: "/faq",
			icon: HelpCircle,
			label: "FAQ",
			featureFlag: "faq",
		},
	];

	// Helper function to filter visible navigation items
	const getVisibleNavigationItems = () => {
		return navigationItems.filter((item) => {
			// Members can only see Overview
			if (isMember && item.to !== "/") {
				return false;
			}

			if (process.env.NODE_ENV === "development") {
				return true;
			}

			if (item.featureFlag === "teams") {
				return isTeamsEnabled;
			}

			return true;
		});
	};

	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	};

	return (
		<>
			<header className="w-full bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex justify-between items-center h-16">
						<div className="flex items-center gap-2">
							<img src="/logo.png" alt="logo" className="h-10" />
						</div>

						{/* Desktop Navigation */}
						<nav className="hidden md:flex items-center gap-6">
							{getVisibleNavigationItems().map((item) => {
								const IconComponent = item.icon;
								return (
									<Link
										key={item.to}
										to={item.to}
										className="flex items-center gap-2 text-gray-600 hover:text-orange-500 transition-colors"
									>
										<IconComponent className="w-5 h-5" />
										<span>{item.label}</span>
									</Link>
								);
							})}
							<div className="flex items-center gap-4">
								{isLoaded && !isSignedIn && (
									<>
										<SignInButton />
										<SignUpButton />
									</>
								)}
								{isLoaded && isSignedIn && <UserButton />}
							</div>
						</nav>

						{/* Mobile Menu Section */}
						<div className="md:hidden flex items-center gap-4">
							{/* Mobile UserButton */}
							{isLoaded && isSignedIn && <UserButton />}

							{/* Mobile Menu Button */}
							<button
								type="button"
								onClick={toggleMobileMenu}
								className="flex flex-col justify-center items-center w-8 h-8 space-y-1 group"
								aria-label="Toggle mobile menu"
							>
								<span
									className={`block w-6 h-0.5 bg-gray-600 transition-all duration-300 ease-in-out group-hover:bg-orange-500 ${
										isMobileMenuOpen ? "rotate-45 translate-y-1.5" : ""
									}`}
								/>
								<span
									className={`block w-6 h-0.5 bg-gray-600 transition-all duration-300 ease-in-out group-hover:bg-orange-500 ${
										isMobileMenuOpen ? "opacity-0" : ""
									}`}
								/>
								<span
									className={`block w-6 h-0.5 bg-gray-600 transition-all duration-300 ease-in-out group-hover:bg-orange-500 ${
										isMobileMenuOpen ? "-rotate-45 -translate-y-1.5" : ""
									}`}
								/>
							</button>
						</div>
					</div>
				</div>
			</header>

			{/* Mobile Menu Overlay */}
			{isMobileMenuOpen && (
				<div className="fixed inset-0 z-40 md:hidden">
					{/* Backdrop */}
					<div
						className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
						onClick={toggleMobileMenu}
						onKeyDown={(e) => {
							if (e.key === "Escape") {
								toggleMobileMenu();
							}
						}}
						role="button"
						tabIndex={0}
						aria-label="Close mobile menu"
					/>

					{/* Menu Content */}
					<div className="fixed inset-y-0 right-0 w-full bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
						<div className="flex flex-col h-full pt-20 pb-6 overflow-y-auto">
							<nav className="flex-1 px-6 space-y-1">
								{getVisibleNavigationItems().map((item) => {
									const IconComponent = item.icon;
									return (
										<Link
											key={item.to}
											to={item.to}
											onClick={toggleMobileMenu}
											className="flex items-center gap-4 px-4 py-3 text-lg text-gray-600 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
										>
											<IconComponent className="w-6 h-6" />
											<span>{item.label}</span>
										</Link>
									);
								})}
							</nav>

							{/* Mobile Auth Section */}
							{isLoaded && !isSignedIn && (
								<div className="px-6 pt-6 border-t border-gray-200">
									<div className="space-y-3">
										<SignInButton />
										<SignUpButton />
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
}
